import gql from "graphql-tag";

import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

import type {MasteryMap} from "#content-types-package/mastery-types.ts";
import {
    getContentDescriptor,
    type ContentItem,
    type Recommendation,
} from "#content-types-package/practice-types.ts";
// TODO(somewhatabstract, JIRA-XXX2-20210907): Improve our patterns so that we
// can pass a valid ApolloGqlClient from the react components.
// DO NOT COPY - bad example of how to get a ApolloGqlClient.
// eslint-disable-next-line no-restricted-imports
import {getUnsafeApolloGqlClient} from "#data-access/gql/get-unsafe-apollo-gql-client.ts";
import type {FpmPromotions} from "#exercises-package/types/shared-types.ts";

import {
    CLEAR_RECOMMENDATION,
    SET_CURRENT_TOPIC,
    UPDATE_FPM_MASTERY,
    UPDATE_FPM_MASTERY_COMPLETE,
    UPDATE_PROGRESS,
    UPDATE_QUIZ_SCORE,
    UPDATE_RECOMMENDATIONS,
    UPDATE_TASK_STATUS,
} from "./user-info-constants.ts";
import type {Action} from "./user-info-reducer.ts";

export const setCurrentTopic = (
    topicId: string,
    masteryEnabled: boolean,
): Action => ({
    type: SET_CURRENT_TOPIC,
    topicId,
    masteryEnabled,
});

type ClearRecommendatationData = {
    clearRecommendationForContentItemWeb:
        | {
              wasSuccessful: boolean;
          }
        | null
        | undefined;
};

type ClearRecommendationVariables = {
    contentDescriptor: string;
    unitId: string;
};

const CLEAR_RECOMMENDATION_FOR_CONTENT_ITEM_WEB_MUTATION: GqlOperation<
    ClearRecommendatationData,
    ClearRecommendationVariables
> = gqlOp(gql`
    mutation clearRecommendationForContentItemWeb(
        $contentDescriptor: String!
        $unitId: String!
    ) {
        clearRecommendation(
            contentDescriptor: $contentDescriptor
            unitId: $unitId
        ) {
            wasSuccessful
        }
    }
`);

export const clearForItem = (
    contentItem: ContentItem,
    topicId: string,
): Action => {
    // Clear recommendations for the item, but optimistically update the
    // application state.
    // TODO(somewhatabstract, JIRA-XXX2-20210907): Pass a valid ApolloGqlClient from
    // the react components calling this.
    // eslint-disable-next-line no-restricted-syntax
    const gqlClient = getUnsafeApolloGqlClient();
    gqlClient.mutate(CLEAR_RECOMMENDATION_FOR_CONTENT_ITEM_WEB_MUTATION, {
        variables: {
            unitId: topicId,
            contentDescriptor: getContentDescriptor(contentItem),
        },
    });

    return {
        type: CLEAR_RECOMMENDATION,
        contentId: contentItem.contentId,
        // Only "Exercise" | "Video" can be cleared
        // Allow all content recommendation kinds to be cleared
        // @ts-expect-error - TS2322 - Type '"Exercise" | "Article" | "Video" | "Challenge" | "Talkthrough" | "Project" | "Interactive" | "Scratchpad"' is not assignable to type '"Exercise" | "Article" | "Video"'.
        kind: contentItem.kind,
    };
};

export const retryQuiz = (slug: string): Action => ({
    type: UPDATE_TASK_STATUS,
    taskKey: slug,
    inProgress: false,
});

export const startQuiz = (slug: string): Action => ({
    type: UPDATE_TASK_STATUS,
    taskKey: slug,
    inProgress: true,
});

export const reportQuizCompletion = (
    slug: string,
    attemptHistory: Array<{
        correct: boolean;
    }>,
): Action => ({
    type: UPDATE_QUIZ_SCORE,
    quizKey: slug,
    quizScore: {
        completedDate: new Date(),
        numCorrect: attemptHistory.filter((attempt) => attempt.correct).length,
        numAttempted: attemptHistory.length,
    },
});

type FPMMastery = {
    masteryMap: MasteryMap;
    masteryPercentage: number | null | undefined;
    masteryPointsEarned: number | null | undefined;
    masteryPointsAvailable: number | null | undefined;
};

export const updateFpmMastery = ({
    masteryMap,
    masteryPercentage,
    masteryPointsEarned,
    masteryPointsAvailable,
}: FPMMastery): Action => ({
    type: UPDATE_FPM_MASTERY,
    masteryMap,
    masteryPercentage,
    masteryPointsEarned,
    masteryPointsAvailable,
});

export const updateFpmMasteryComplete = (): Action => ({
    type: UPDATE_FPM_MASTERY_COMPLETE,
});

export const updateProgress = (fpmPromotions: FpmPromotions): Action => ({
    type: UPDATE_PROGRESS,
    fpmPromotions,
});

export const updateRecommendations = (
    recommendations: Array<Recommendation>,
): Action => ({
    type: UPDATE_RECOMMENDATIONS,
    recommendations,
});
